import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Chip,
    Button,
    Drawer,
    Divider,
    Switch,
    FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreateEntry from '../pages/entry/CreateEntry';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const VersionDrawer = ({ open, onClose, entry, contentType, onVersionSelect }) => {
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [isCurrentVersionRestored, setIsCurrentVersionRestored] = useState(false);
    const [lastRestoredVersion, setLastRestoredVersion] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const formattedContentType = useMemo(() => {
        // const versionControlFields = contentType?.filter(field => field.version_control != 'a') || [];
        return {
            contentType: {
                _id: entry?.entry?.contentTypeId,
                name: entry?.entry?.content_type_name,
                fields: contentType.fields
            }
        };
    }, [entry, contentType]);

    const versions = useMemo(() => {
        if (!entry?.entry) return [];

        const allVersions = [];
        const entryVersionData = entry.entry.versionData || {};

        allVersions.push({
            versionNumber: entry.entry.version || 1,
            user: entry.entry.createdBy || 'Unknown',
            contentTypeId: entry.entry.contentTypeId,
            data: entry.entry.data || {},
            localeContent: entry.localeContent || [],
            identifier: entry.entry.identifier,
            published: entry.entry.published,
            status: 'current'
        });

        Object.keys(entryVersionData).forEach(version => {
            const versionData = entryVersionData[version];
            const localeContents = entry.localeContent?.map(locale => ({
                ...locale,
                ...locale.versionData?.[version]?.data
            })) || [];

            allVersions.push({
                versionNumber: Number(version),
                user: versionData.user || 'Unknown',
                versionTimestamp: versionData.timeStamp || '-----------',
                contentTypeId: entry.entry.contentTypeId,
                data: versionData.data || {},
                localeContent: localeContents,
                identifier: versionData.identifier || entry.entry.identifier,
                published: versionData.published,
                status: 'previous'
            });
        });

        return allVersions.sort((a, b) => b.versionNumber - a.versionNumber);
    }, [entry]);

    const [compareMode, setCompareMode] = useState(false);

    useEffect(() => {
        if (!open) {
            setSelectedVersion(null);
        } else if (open && versions.length > 0) {
            const currentVersion = versions.find(v => v.status === 'current');
            setSelectedVersion(currentVersion);
            handleVersionClick(currentVersion);
        }
    }, [open, versions]);

    const handleVersionClick = (version) => {
        const timestamp = Date.now();
        setSelectedVersion({
            ...version,
            _timestamp: timestamp,
            localeContent: version.localeContent.map(locale => ({
                ...locale,
                _timestamp: timestamp
            }))
        });
    };

    const handleRestoreVersion = (version) => {
        const formattedData = {
            identifier: version.identifier,
            ...entry.entry.data,
            ...version.data
        };

        version.localeContent.forEach(localeData => {
            const locale = localeData.locale;

            Object.keys(localeData).forEach(key => {
                if (key !== 'locale' && key !== '_id' && key !== 'versionData') {
                    if (!formattedData[key]) {
                        formattedData[key] = {};
                    }
                    formattedData[key][locale] = localeData[key];
                }
            });
        });

        console.log('Formatted Version Data:', formattedData);

        setLastRestoredVersion(version.versionNumber);
        setIsCurrentVersionRestored(version.status !== 'current');
        if (onVersionSelect) {
            onVersionSelect(formattedData);
        }
        onClose();
    };

    useEffect(() => {
        if (!open) {
            setSelectedVersion(null);
            setLastRestoredVersion(null);
            setIsCurrentVersionRestored(false);
            setCompareMode(false);
        }
    }, [open]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '80%',
                    display: 'flex',
                    bgcolor: '#FAFAFA'
                }
            }}
        >
            {open && (
                <>
                    <Box sx={{
                        display: 'flex', width: '100%', height: '100%', flexDirection: {
                            xs: 'column',
                            md: 'row',
                        },
                    }}>
                        <Box sx={{
                            width: '300px',
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            height: isMobile ? '250px' : '100%',
                            overflowY: 'auto',
                        }}>
                            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6">Version History</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={compareMode}
                                            onChange={(e) => setCompareMode(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label="Compare"
                                    componentsProps={{ typography: { variant: 'body2' } }}
                                />
                            </Box>

                            <List sx={{ p: 2 }}>
                                {versions.map((version) => (
                                    <ListItem
                                        key={version.versionNumber}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: (selectedVersion?.versionNumber === version.versionNumber)
                                                ? 'primary.main'
                                                : 'divider',
                                            borderRadius: 1,
                                            mb: 1,
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            cursor: 'pointer',
                                            bgcolor: 'background.paper',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                        onClick={() => handleVersionClick(version)}
                                    >
                                        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle2">
                                                Version {version.versionNumber}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={version.status}
                                                color={version.status === 'current' ? 'primary' : 'default'}
                                            />
                                        </Box>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2" color="textSecondary">
                                                    {version.status === 'current' ? (
                                                        <Box component="span" sx={{ fontWeight: 500 }}>
                                                            Current version
                                                        </Box>
                                                    ) : (
                                                        `Modified by ${version.user}`
                                                    )}
                                                </Typography>
                                            }
                                        />
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2" color="textSecondary">
                                                    {version.versionTimestamp && (
                                                        version.versionTimestamp
                                                    )}
                                                </Typography>
                                            }
                                        />

                                        {selectedVersion?.versionNumber === version.versionNumber && !compareMode && (
                                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => handleRestoreVersion(version)}
                                                    disabled={
                                                        (version.status === 'current' && !isCurrentVersionRestored) ||
                                                        (lastRestoredVersion === version.versionNumber)
                                                    }
                                                    sx={{
                                                        '&.Mui-disabled': {
                                                            backgroundColor: 'action.disabledBackground',
                                                            color: 'action.disabled'
                                                        }
                                                    }}
                                                >
                                                    Restore this version
                                                </Button>
                                            </Box>
                                        )}
                                    </ListItem>
                                ))}
                            </List>
                        </Box>

                        <Box sx={{
                            flex: 1,
                            height: '100%',
                            overflowY: 'auto',
                            p: 3,
                            display: 'flex',
                            flexDirection: { xs: 'column', lg: 'row' },
                            gap: 2
                        }}>
                            {entry?.entry && contentType && (
                                compareMode ? (
                                    <>
                                        <Box sx={{
                                            flex: 1,
                                            borderRight: { xs: 'none', lg: '1px solid' },
                                            borderBottom: { xs: '1px solid', lg: 'none' },
                                            borderColor: 'divider',
                                            pr: { xs: 0, lg: 2 },
                                            pb: { xs: 2, lg: 0 }
                                        }}>
                                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Current Version</Typography>
                                            <CreateEntry
                                                contentType={formattedContentType}
                                                editEntry={{
                                                    entry: entry.entry,
                                                    localeContent: entry.localeContent,
                                                }}
                                                isDrawer={true}
                                                isDrawerEdit={true}
                                                isVersionPreview={true}
                                                // Using a static key for current version to avoid re-renders unless entry changes
                                                key={`current-${entry.entry?.version || 'new'}`}
                                                compareWith={{
                                                    entry: {
                                                        ...entry.entry,
                                                        data: selectedVersion?.data || entry.entry.data,
                                                    },
                                                    localeContent: selectedVersion?.localeContent || entry.localeContent,
                                                }}
                                                diffType="current"
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, pl: { xs: 0, lg: 2 }, pt: { xs: 2, lg: 0 } }}>
                                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                                                Selected Version {selectedVersion?.versionNumber ? `(${selectedVersion.versionNumber})` : ''}
                                            </Typography>
                                            {selectedVersion && (
                                                <CreateEntry
                                                    contentType={formattedContentType}
                                                    editEntry={{
                                                        entry: {
                                                            ...entry.entry,
                                                            data: selectedVersion?.data || entry.entry.data,
                                                            identifier: selectedVersion?.identifier || entry.entry.identifier,
                                                            contentTypeId: selectedVersion?.contentTypeId || entry.entry.contentTypeId,
                                                            references: entry.entry.references || {}
                                                        },
                                                        localeContent: selectedVersion?.localeContent || entry.localeContent,
                                                    }}
                                                    key={`selected-${selectedVersion?._timestamp}`}
                                                    isDrawer={true}
                                                    isDrawerEdit={true}
                                                    isVersionPreview={true}
                                                    compareWith={{
                                                        entry: entry.entry,
                                                        localeContent: entry.localeContent,
                                                    }}
                                                    diffType="selected" // or "historical"
                                                />
                                            )}
                                            {selectedVersion && selectedVersion.versionNumber !== versions.find(v => v.status === 'current')?.versionNumber && (
                                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                                    <Button
                                                        variant="contained"
                                                        onClick={() => handleRestoreVersion(selectedVersion)}
                                                        disabled={lastRestoredVersion === selectedVersion.versionNumber}
                                                    >
                                                        Restore this version
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </>
                                ) : (
                                    <CreateEntry
                                        contentType={formattedContentType}
                                        editEntry={{
                                            entry: {
                                                ...entry.entry,
                                                data: selectedVersion?.data || entry.entry.data,
                                                identifier: selectedVersion?.identifier || entry.entry.identifier,
                                                contentTypeId: selectedVersion?.contentTypeId || entry.entry.contentTypeId,
                                                references: entry.entry.references || {}
                                            },
                                            localeContent: selectedVersion?.localeContent || entry.localeContent,
                                        }}
                                        key={selectedVersion?._timestamp}
                                        isDrawer={true}
                                        isDrawerEdit={true}
                                        isVersionPreview={true}
                                    />
                                )
                            )}
                        </Box>
                    </Box>
                </>
            )}
        </Drawer>
    );
};

export default VersionDrawer;